import { useState } from 'react'

import {
  Box,
  Button as DeprecatedButton,
  Popover,
  Spinner,
  Text,
} from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { Warning } from '@factorialco/f0-react/icons/app'
import { CompensationValidationFragment } from 'generated/resources/graphql'

import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import useDeleteCompensationsEmployeesCompensationsValidation from 'modules/compensations/hooks/useDeleteCompensationsEmployeesCompensationsValidation'

interface CompensationsAggregatesAlertPopoverProps {
  readonly?: boolean
  validation?: CompensationValidationFragment
  inputAmount: number
  resultAmount?: number
  isLoading: boolean
}

export function CompensationsAggregatesAlertPopover({
  readonly,
  validation,
  inputAmount,
  resultAmount,
  isLoading,
}: CompensationsAggregatesAlertPopoverProps) {
  const [isWarningPopoverOpen, setIsWarningPopoverOpen] = useState(false)
  const { deleteValidation, loading } = useDeleteCompensationsEmployeesCompensationsValidation()

  if (!validation && !isLoading) {
    return null
  }

  const handleDeleteValidation = async (validationId: number | undefined) => {
    if (!validationId) {
      return
    }

    const result = await deleteValidation({
      id: validationId,
    })

    const handled = handleResult(result, (_errors) => {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('common.error'),
      })
    })

    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('common.saved_successfully'),
      })
      setIsWarningPopoverOpen(false)
    }
  }

  const getWarningTitle = () => {
    if (!resultAmount) {
      return i18n.t(
        'compensations.PayrollRunCompensationsList.alertsSidePanelButton.discrepancy.title_values_missing'
      )
    }

    return i18n.t(
      'compensations.PayrollRunCompensationsList.alertsSidePanelButton.discrepancy.title_values_discrepancy'
    )
  }

  const getWarningDescription = () => {
    if (!resultAmount) {
      return i18n.t(
        'compensations.PayrollRunCompensationsList.alertsSidePanelButton.discrepancy.description_values_missing'
      )
    }

    if (resultAmount < inputAmount) {
      return i18n.t(
        'compensations.PayrollRunCompensationsList.alertsSidePanelButton.discrepancy.description_values_discrepancy_lower_than_input'
      )
    }

    return i18n.t(
      'compensations.PayrollRunCompensationsList.alertsSidePanelButton.discrepancy.description_values_discrepancy_higher_than_input'
    )
  }

  const warningTitle = getWarningTitle()
  const warningDescription = getWarningDescription()
  const warningButtonText = i18n.t(
    'compensations.PayrollRunCompensationsList.alertsSidePanelButton.discrepancy.button'
  )

  return (
    <Box gap="s4">
      {isLoading && <Spinner size="s16" />}
      {!isLoading && (
        <Popover.ItemList
          attachment="top right"
          targetAttachment="bottom center"
          toggle={() => () => setIsWarningPopoverOpen(!isWarningPopoverOpen)}
          open={isWarningPopoverOpen}
          gapDivider={false}
        >
          <F0Button
            variant="outline"
            icon={Warning}
            hideLabel
            size="sm"
            label="Warning"
            type="button"
            disabled={readonly}
          />
          <Box gap="s8">
            <Box gap="s4" alignItems="flexStart">
              <Text.Header3 color="white">{warningTitle}</Text.Header3>

              <Text.Normal color="white" size="100">
                {warningDescription}
              </Text.Normal>
            </Box>

            <Box alignItems="center" width="full" paddingTop="s6">
              <DeprecatedButton
                onClick={() => handleDeleteValidation(validation?.id)}
                type="secondary-dark"
                disabled={loading || readonly}
              >
                {warningButtonText}
              </DeprecatedButton>
            </Box>
          </Box>
        </Popover.ItemList>
      )}
    </Box>
  )
}
